import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function AdminRestricted() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ pincode: "", area: "" });
  const [loading, setLoading] = useState(true);
  const [restrictionEnabled, setRestrictionEnabled] = useState(true);
  const [restrictionLoading, setRestrictionLoading] = useState(true);
  const [savingRestriction, setSavingRestriction] = useState(false);

  const load = () => api.get("/admin/available-pincodes")
    .then((r) => setRows(r.data))
    .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message))
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/admin/pincode-restriction")
      .then((r) => setRestrictionEnabled(r.data.enabled))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setRestrictionLoading(false));
  }, []);

  const toggleRestriction = async () => {
    setSavingRestriction(true);
    try {
      const { data } = await api.put("/admin/pincode-restriction", { enabled: !restrictionEnabled });
      setRestrictionEnabled(data.enabled);
      toast.success(data.enabled ? "Pincode restrictions enabled" : "Pincode restrictions disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingRestriction(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/available-pincodes", form);
      toast.success("Available pincode added");
      setForm({ pincode: "", area: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const del = async (r) => {
    const impact = restrictionEnabled ? " Orders to this pincode will be blocked." : "";
    if (!window.confirm(`Remove ${r.pincode} from available delivery areas?${impact}`)) return;
    try {
      await api.delete(`/admin/available-pincodes/${r.id}`);
      toast.success(`${r.pincode} removed from available pincodes`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    load();
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
        <MapPin className="w-6 h-6 text-hyd-gold" /> Available pincodes
      </h1>
      <p className="text-hyd-text2 text-sm">
        {restrictionEnabled
          ? "Customers can place orders only to pincodes listed here."
          : "Pincode restrictions are disabled. Customers can place orders to any valid pincode."}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant={restrictionEnabled ? "destructive" : "default"}
          onClick={toggleRestriction}
          disabled={restrictionLoading || savingRestriction}
          className={!restrictionEnabled ? "bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold" : ""}
        >
          {restrictionLoading
            ? "Loading restriction..."
            : savingRestriction
              ? "Saving..."
              : restrictionEnabled
                ? "Disable pincode restrictions"
                : "Enable pincode restrictions"}
        </Button>
        <span className="text-sm text-hyd-text2">
          {restrictionEnabled ? "Enabled: only listed pincodes are accepted." : "Disabled: all valid pincodes are accepted."}
        </span>
      </div>

      <form onSubmit={submit} className="mt-5 bg-hyd-panel border border-hyd-border rounded-xl p-4 grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs text-hyd-text2 mb-1 block">Pincode</Label>
          <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="bg-hyd-bg border-hyd-border" inputMode="numeric" maxLength={6} minLength={6} required />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-hyd-text2 mb-1 block">Area / locality</Label>
          <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="bg-hyd-bg border-hyd-border" placeholder="Optional" />
        </div>
        <Button type="submit" disabled={form.pincode.length !== 6} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold sm:col-span-3"><Plus className="w-4 h-4 mr-1" /> Add available pincode</Button>
      </form>

      <div className="mt-5 bg-hyd-panel border border-hyd-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Pincode</th><th className="text-left p-3">Area / locality</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-hyd-border">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3 font-mono">{r.pincode}</td>
                <td className="p-3 text-hyd-text2">{r.area || "Hyderabad"}</td>
                <td className="p-3 text-right"><Button variant="ghost" size="sm" aria-label={`Remove ${r.pincode}`} onClick={() => del(r)} className="text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-8 text-center text-hyd-text3">Loading available pincodes...</div>}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-hyd-text3">
            {restrictionEnabled
              ? "No available pincodes yet. Orders will be blocked until pincodes are added."
              : "No available pincodes yet. All valid pincodes are accepted while restrictions are disabled."}
          </div>
        )}
      </div>
    </div>
  );
}
